import '@shui/core/sh-button';

import { Component, booleanAttribute, input, output, ChangeDetectionStrategy } from '@angular/core';

export type ShButtonColor = 'primary' | 'primary-neutral' | 'secondary' | 'tertiary' | 'danger';
export type ShButtonLayout = 'normal' | 'compact' | 'classic';
export type ShButtonShape = 'pill-s' | 'pill-m' | 'pill-l' | 'circle';
export type ShButtonSize = 's' | 'm' | 'l' | 'auto' | 'max-content' | 'full-width';

@Component({
    selector: 'sh-button',
    template: '<ng-content />',
    imports: [],
    changeDetection: ChangeDetectionStrategy.Eager,
    host: {
        '[attr.label]': 'label()',
        '[attr.color]': 'color()',
        '[attr.disabled]': 'disabled() ? "" : null',
        '[attr.href]': 'href() ?? null',
        '[attr.icon]': 'icon() ?? null',
        '[attr.icon-position]': 'iconPosition() ?? null',
        '[attr.layout]': 'layout()',
        '[attr.shape]': 'shape() ?? null',
        '[attr.size]': 'size()',
        '[attr.tooltip]': 'tooltip() ?? null',
        '[attr.width]': 'width() ?? null',
        '[attr.aria-label]': 'ariaLabel() ?? label()',
        '(click)': 'handleClick($event)'
    }
})
export class ShButtonComponent {
    /**
     * Defines the button's label in all shapes (in circle shape it will only be shown if tooltip is
     * also defined).
     */
    public readonly label = input('Label');
    /**
     * Defines the color of the button. Possible values are `primary`, 'primary-neutral',
     * `secondary`, `tertiary`, `danger`, `success`.
     */
    public readonly color = input<ShButtonColor>('primary');
    /** If set, disabled styles will be applied */
    public readonly disabled = input(false, { transform: booleanAttribute });
    /** Defines a path to which navigation will occur on click. */
    public readonly href = input<string | undefined>(undefined);
    /** Defines the button's icon. Only applies to circle shape. */
    public readonly icon = input<string | undefined>(undefined);
    /**
     * Defines the position of icon . Possible value: `left`. (If specified, the icon is shown
     * before the label. For available icon list, refer Icon page.)
     */
    public readonly iconPosition = input<'left' | undefined>(undefined);
    /**
     * Defines the button layout. Defaults to `classic`, to maintain full compatibility with
     * previous Shui versions. `normal` introduces a larger button variant, while `compact` is
     * optimized for space-constrained interfaces.
     */
    public readonly layout = input<ShButtonLayout>('classic');
    /**
     * Defines the shape of the button. Possible values are `pill-s`, `pill-m`, `pill-l`, `circle`.
     */
    public readonly shape = input<ShButtonShape | undefined>(undefined);
    /**
     * If icon is not set, defines the size of the button. Possible values are `s`, `m`, `l`, `auto`
     * (in which size grows based on label's length with `l` size being the maximum viable width),
     * `max-content` (in which size grows based on label's length without having a maximum width)
     * and `full-width` (in which size covers the whole width of the parent container independent of
     * label's length. This variation should be used for mobile's use case only).
     */
    public readonly size = input<ShButtonSize>('s');
    /** Defines the position of the tooltip (if an icon and label are set). */
    public readonly tooltip = input<string | undefined>(undefined);
    /**
     * Sets the button width for `normal` and `compact` layouts. Accepts any valid CSS width value.
     * Defaults to `auto` with a preset max-width. Not applicable to `classic` layout.
     */
    public readonly width = input<string | undefined>(undefined);
    /** Accessible label for screen readers. Defaults to label when not set. */
    public readonly ariaLabel = input<string | undefined>(undefined);

    public readonly clicked = output<void>();

    protected handleClick(event: Event): void {
        if (this.disabled()) {
            event.stopImmediatePropagation();
            return;
        }
        this.clicked.emit();
    }
}
